import { useState } from "react";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import ButtonWithConfirm from "../../components/ButtonWithConfirm";
import { Loading } from "../../components/Loading";
import DemoUsersTable from "./DemoUsersTable";
import { useGetDemoQuery, useResetDemoMutation } from "./demoApiSlice";

interface DemoAlert {
	level: AlertLevel;
	message: string;
}

export default function Demo() {
	const { data, isLoading, isFetching } = useGetDemoQuery();
	const [resetDemo, { isLoading: isResetting }] = useResetDemoMutation();
	const [alert, setAlert] = useState<DemoAlert | null>(null);

	const hasDemo = !!data && !!data.schoolName;

	const runReset = (successMessage: string, errorMessage: string) => {
		setAlert(null);
		resetDemo()
			.unwrap()
			.then(() => setAlert({ level: AlertLevel.Info, message: successMessage }))
			.catch(() => setAlert({ level: AlertLevel.Error, message: errorMessage }));
	};

	const alertBanner = alert && (
		<AlertMessage
			className="mt-4 text-sm"
			type={alert.level}
			message={alert.message}
			showIcon={alert.level === AlertLevel.Error}
			onClear={() => setAlert(null)}
		/>
	);

	if (isLoading) {
		return (
			<section>
				<Loading loading={true} />
			</section>
		);
	}

	if (!hasDemo) {
		return (
			<section>
				<h1>Demo</h1>
				{alertBanner}
				<div className="mt-6">
					<p className="text-sm text-gray-600">You don't have any demo schools set up.</p>
					<button
						id="create-demo-button"
						className="btn btn-primary mt-4"
						disabled={isResetting}
						onClick={() =>
							runReset(
								"Your demo sandbox has been created.",
								"There was an error creating the demo sandbox."
							)
						}>
						{isResetting ? "Creating…" : "Create One Now"}
					</button>
				</div>
				<Loading loading={isResetting} />
			</section>
		);
	}

	return (
		<section>
			<h1>{data!.schoolName}</h1>
			{alertBanner}
			<div className="flex justify-between items-end gap-4 mt-4">
				<p className="max-w-3xl">
					Click Reset when you're ready to abandon this demo and start fresh. All the
					changes you've made since the last reset will be undone and new data will be
					generated.
				</p>
				<ButtonWithConfirm
					id="reset-demo-button"
					className="btn btn-danger shrink-0"
					title="Reset demo sandbox?"
					message="All the changes you've made since the last reset will be undone and new data will be generated."
					type={AlertLevel.Warning}
					confirmLabel="Reset"
					cancelLabel="Cancel"
					disabled={isResetting}
					onConfirm={() =>
						runReset(
							"The demo sandbox has been reset with fresh data.",
							"There was an error resetting the demo sandbox."
						)
					}>
					{isResetting ? "Resetting…" : "Reset"}
				</ButtonWithConfirm>
			</div>
			<Loading loading={isFetching || isResetting} />
			<DemoUsersTable users={data!.users} />
		</section>
	);
}
